import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AlertButton } from '../components/Templates/ElmerModalAlert';
import callSeialInfoApi from '../apis/callSerialInfoApi';
import callLogoffApi from '../apis/callLogoffApi';
import callProgressResetApi from '../apis/callProgressResetApi';
import useAccessKey from './useAccessKey';
import useResume from './useResume';
import callSentenceStatusAPI from '../apis/callSentenceStatusAPI';
import { openHelpPage, openAttentionPage } from '../utils/link';
import { PAGE_PATH_NAME } from '../utils/const';

// メニュー一覧のonClick類
export type MenuOnClick = {
  // シリアル情報ボタン押下時処理
  onClickSerialInfo: () => void;
  // 学習進捗リセットボタン押下時処理
  onClickResetLearningHistory: () => void;
  // 登録解除ボタン押下時処理
  onClickLogOff: () => void;
  // ヘルプボタン押下時処理
  onClickHelp: () => void;
  // ご注意ボタン押下時処理
  onClickAttention: () => void;
};

// 共通ヘッダーアラート種別
const HEADER_ALERT_TYPES = {
  // 学習進捗リセット確認
  CHECK_RESET_PROGRESS: 'CHECK_RESET_PROGRESS',
  // シリアル情報取得失敗
  FAILED_SERIAL_INFO: 'FAILED_SERIAL_INFO',
  // 学習進捗リセット失敗
  FAILED_RESET_PROGRESS: 'FAILED_RESET_PROGRESS',
  // 登録解除失敗
  FAILED_LOGOFF: 'FAILED_LOGOFF',
  // 自動ログオフ
  INFO_LOGOFF: 'INFO_LOGOFF',
} as const;
type HEADER_ALERT_TYPES =
  typeof HEADER_ALERT_TYPES[keyof typeof HEADER_ALERT_TYPES];

// 共通ヘッダーアラートに関する処理
export type HeaderAlert = {
  type: HEADER_ALERT_TYPES | null;
  changeHeaderAlertType: (type: HEADER_ALERT_TYPES | null) => void;
  alertTitle: string;
  alertText: string;
  alertButtons: Array<AlertButton>;
  onClickCloseModal: () => void;
};

// シリアル情報ダイアログに関する処理
export type SerialInfoDialog = {
  isOpen: boolean;
  onClickClose: () => void;
  serialNumber: string;
  courseName: string;
  productName: string;
  onClickCopy: () => void;
};

// 本カスタムフックの返り値
export type UseHeaderValue = {
  // アクセスキー
  accessKey: string;
  // 戻るボタン表示状態
  isAppearBackButton: boolean;
  // メニュー表示状態
  isOpenMenu: boolean;
  // 表示中画面のパス
  pathName: string;
  // APIコール中かどうか
  isCallingApi: boolean;
  // メニューボタン押下時処理
  onClickMenuButton: () => void;
  // 戻るボタン押下時処理
  onClickBackButton: () => void;
  // メニュー一覧のonClick類
  menuOnClick: MenuOnClick;
  // 共通ヘッダーアラート
  headerAlert: HeaderAlert;
  // シリアル情報
  serialInfoDialog: SerialInfoDialog;
  // ヘッダメニューの要素のref
  headerMenuRef: React.RefObject<HTMLDivElement>;
  // メニューを開くボタンのref
  openHeaderMenuButtonRef: React.RefObject<HTMLButtonElement>;
};

const useHeader = (): UseHeaderValue => {
  // state
  // アクセスキー
  const { accessKey, reset } = useAccessKey();
  const { resume } = useResume();
  // 戻るボタン表示状態
  const [isAppearBackButton, setIsApperBackButton] = useState<boolean>(false);
  // メニュー表示状態
  const [isOpenMenu, setIsOpenMenu] = useState<boolean>(false);
  // 表示中画面のパス
  const [pathName, setPathName] = useState<string>('');
  // APIコール中かどうか
  const [isCallingApi, setIsCallingApi] = useState<boolean>(false);
  // シリアル情報ダイアログ表示状態
  const [isOpenSerialInfo, setIsOpenSerialInfo] = useState<boolean>(false);
  // シリアル番号
  const [serialNumber, setSerialNumber] = useState<string>('');
  // コース名
  const [courseName, setCourseName] = useState<string>('');
  // 製品名
  const [productName, setProductName] = useState<string>('');
  // メニューの要素のref
  const headerMenuRef = useRef<HTMLDivElement>(null);
  // メニューを開くボタンのref
  const openHeaderMenuButtonRef = useRef<HTMLButtonElement>(null);

  // アラート状態
  const [headerAlertType, setHeaderAlertType] =
    useState<HEADER_ALERT_TYPES | null>(null);

  // 履歴管理
  const history = useHistory();
  const location = useLocation();

  /**
   * メニュー外クリック時の動作
   * メニューボタンとメニュー以外をクリックしたら、メニューを閉じる
   * @param e イベント
   */
  const handleMenuOutsideClick = (e: Event) => {
    if (
      openHeaderMenuButtonRef.current &&
      e.target instanceof Node &&
      openHeaderMenuButtonRef.current.contains(e.target)
    ) {
      return;
    }
    if (
      headerMenuRef.current &&
      e.target instanceof Node &&
      !headerMenuRef.current.contains(e.target)
    ) {
      setIsOpenMenu(false);
    }
  };

  // 初期処理
  useEffect(() => {
    document.addEventListener('mousedown', handleMenuOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleMenuOutsideClick);
    };
  }, []);

  useEffect(() => {
    setIsApperBackButton(false);

    const path = location.pathname;
    setPathName(path);
    if (path === '/training') {
      setIsApperBackButton(true);
    }
  }, [location]);

  /**
   * ハンバーガーメニューボタン押下時処理
   */
  const onClickMenuButton = () => {
    setIsOpenMenu(!isOpenMenu);
  };

  /**
   * 戻るボタン押下時処理
   */
  const onClickBackButton = async () => {
    if (pathName === '/training') {
      setIsCallingApi(true);

      // センテンス再生状況設定APIの実行(処理が完了するまで遷移しない)
      void (await callSentenceStatusAPI({
        accesskey: accessKey,
        trainingId: resume.trainingId,
        sentenceId: resume.sentenceId,
      }));

      setIsCallingApi(false);
    }
    history.goBack();
  };

  /**
   * 共通ヘッダーアラートを設定(nullは非表示)
   * @param type アラート種別
   */
  const changeHeaderAlertType = (type: HEADER_ALERT_TYPES | null) => {
    setHeaderAlertType(type);
  };

  /**
   * アラートを非表示にする
   */
  const onClickCloseModal = () => {
    setHeaderAlertType(null);
  };

  /**
   * ×ボタン押下時処理
   */
  const onClickClose = () => {
    setIsOpenSerialInfo(false);
  };

  /**
   * [コピー]ボタン押下時処理
   */
  const onClickCopy = () => {
    void navigator.clipboard.writeText(serialNumber);
  };

  /**
   * メニュー一覧 シリアル情報ボタン押下時処理
   */
  const onClickSerialInfo = async () => {
    try {
      setIsOpenMenu(!isOpenMenu);

      setIsCallingApi(true);

      // シリアル情報取得API呼び出し
      const res = await callSeialInfoApi({ accesskey: accessKey });
      if (res.resultCode === 'INFO_LOGOFF') {
        throw HEADER_ALERT_TYPES.INFO_LOGOFF;
      } else if (res.resultCode !== 'OK') {
        throw HEADER_ALERT_TYPES.FAILED_SERIAL_INFO;
      }

      // モーダルウィンドウに情報をセット
      setSerialNumber(res.serial.number);
      setCourseName(res.serial.courseName);
      setProductName(res.serial.productName);
      setIsOpenSerialInfo(true);
    } catch (error) {
      if (error === 'INFO_LOGOFF') {
        changeHeaderAlertType(HEADER_ALERT_TYPES.INFO_LOGOFF);
      } else {
        // シリアル情報取得エラーアラート表示
        changeHeaderAlertType(HEADER_ALERT_TYPES.FAILED_SERIAL_INFO);
      }
    } finally {
      setIsCallingApi(false);
    }
  };

  /**
   * メニュー一覧 学習進捗リセットボタン押下時処理
   */
  const onClickResetLearningHistory = () => {
    setIsOpenMenu(!isOpenMenu);

    // 確認モーダル表示
    changeHeaderAlertType(HEADER_ALERT_TYPES.CHECK_RESET_PROGRESS);
  };

  /**
   * 学習進捗リセット処理
   */
  const resetTrainingHistory = useCallback(async () => {
    try {
      changeHeaderAlertType(null);

      setIsCallingApi(true);

      // 学習進捗リセットAPI呼び出し
      const res = await callProgressResetApi({ accesskey: accessKey });
      if (res.resultCode === 'INFO_LOGOFF') {
        throw HEADER_ALERT_TYPES.INFO_LOGOFF;
      } else if (res.resultCode !== 'OK') {
        throw HEADER_ALERT_TYPES.FAILED_RESET_PROGRESS;
      }

      // 画面再描画
      history.go(0);
    } catch (error) {
      if (error === 'INFO_LOGOFF') {
        changeHeaderAlertType(HEADER_ALERT_TYPES.INFO_LOGOFF);
      } else {
        changeHeaderAlertType(HEADER_ALERT_TYPES.FAILED_RESET_PROGRESS);
      }
    } finally {
      setIsCallingApi(false);
    }
  }, [history, accessKey]);

  /**
   * メニュー一覧 登録解除ボタン押下時処理
   */
  const onClickLogOff = async () => {
    try {
      setIsOpenMenu(!isOpenMenu);

      setIsCallingApi(true);

      // ログオフAPI呼び出し
      const res = await callLogoffApi({ accesskey: accessKey });
      if (res.resultCode !== 'OK') {
        throw new Error();
      }

      // ローカルストレージのアクセスキー削除
      reset();

      // シリアル入力画面に遷移
      history.replace(PAGE_PATH_NAME.SERIAL_INPUT);
    } catch {
      // ログオフエラーアラート表示
      changeHeaderAlertType(HEADER_ALERT_TYPES.FAILED_LOGOFF);
    } finally {
      setIsCallingApi(false);
    }
  };

  /**
   * メニュー一覧 ヘルプボタン押下時処理
   */
  const onClickHelp = () => {
    setIsOpenMenu(false);
    openHelpPage();
  };

  /**
   * メニュー一覧 ご注意ボタン押下時処理
   */
  const onClickAttention = () => {
    setIsOpenMenu(false);
    openAttentionPage();
  };

  const alertTitle = useMemo(() => {
    switch (headerAlertType) {
      case HEADER_ALERT_TYPES.CHECK_RESET_PROGRESS:
        return '学習進捗をリセットしますか？';
      case HEADER_ALERT_TYPES.FAILED_RESET_PROGRESS:
        return '学習進捗リセットエラー';
      case HEADER_ALERT_TYPES.FAILED_SERIAL_INFO:
        return 'シリアル情報取得エラー';
      case HEADER_ALERT_TYPES.FAILED_LOGOFF:
        return '認証解除エラー';
      default:
        return '';
    }
  }, [headerAlertType]);

  const alertText = useMemo(() => {
    switch (headerAlertType) {
      case HEADER_ALERT_TYPES.FAILED_SERIAL_INFO:
        return 'シリアル情報の取得時にエラーが発生しました。\nお手数ですが、時間をおいて再度お試しください。';
      case HEADER_ALERT_TYPES.FAILED_RESET_PROGRESS:
        return '学習進捗のリセット中にエラーが発生しました。\nお手数ですが、時間をおいて再度お試しください。';
      case HEADER_ALERT_TYPES.FAILED_LOGOFF:
        return '認証解除時にエラーが発生しました。\nお手数ですが、時間をおいて再度お試しください。';
      default:
        return '';
    }
  }, [headerAlertType]);

  const alertButtons = useMemo(() => {
    switch (headerAlertType) {
      case HEADER_ALERT_TYPES.CHECK_RESET_PROGRESS:
        return [
          { label: 'リセット', onClick: resetTrainingHistory },
          { label: 'キャンセル', onClick: () => changeHeaderAlertType(null) },
        ];
      case HEADER_ALERT_TYPES.FAILED_SERIAL_INFO:
      case HEADER_ALERT_TYPES.FAILED_RESET_PROGRESS:
      case HEADER_ALERT_TYPES.FAILED_LOGOFF:
        return [
          { label: '閉じる', onClick: () => changeHeaderAlertType(null) },
        ];
      default:
        return [];
    }
  }, [headerAlertType, resetTrainingHistory]);

  return {
    accessKey,
    isAppearBackButton,
    isOpenMenu,
    pathName,
    isCallingApi,
    onClickMenuButton,
    onClickBackButton,
    menuOnClick: {
      onClickSerialInfo,
      onClickResetLearningHistory,
      onClickLogOff,
      onClickHelp,
      onClickAttention,
    },
    headerAlert: {
      type: headerAlertType,
      changeHeaderAlertType,
      alertTitle,
      alertText,
      alertButtons,
      onClickCloseModal,
    },
    serialInfoDialog: {
      isOpen: isOpenSerialInfo,
      onClickClose,
      serialNumber,
      courseName,
      productName,
      onClickCopy,
    },
    headerMenuRef,
    openHeaderMenuButtonRef,
  };
};

export default useHeader;
